import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatMoneyFromCents } from "../../../helpers/money";
import {
  getMessagingSettings,
  getMessagingOutboundPreview,
  isWhatsAppUsageBlocked,
  isWhatsAppUsageNearLimit,
  IUGIS_REMINDER_HOUR_OPTIONS,
  MAX_IUGIS_REMINDER_HOURS,
  MESSAGING_REMINDER_HOUR_PRESETS,
  MessagingChannelMode,
  MessagingHealth,
  MessagingOutboundPreview,
  MessagingPackCatalogItem,
  MessagingSettings,
  MessagingSettingsData,
  MessagingUsage,
  NotificationChannelPreference,
  purchaseMessagingPack,
  updateMessagingSettings,
} from "../../../services/messagingSettingsService";

const DEFAULT_SETTINGS: MessagingSettings = {
  messaging_channel_mode: "pooled",
  notification_channel_preference: "both",
  client_contact_whatsapp: null,
  appointment_created_notifications_enabled: true,
  appointment_reminder_notifications_enabled: true,
  appointment_reminder_hours_before: 24,
  appointment_reminder_hours_before_list: [24],
  notify_on_created_default: true,
  notify_reminder_default: true,
};

const DEFAULT_USAGE: MessagingUsage = {
  sent_count: 0,
  pack_bonus: 0,
  limit: 0,
  effective_limit: 0,
  remaining_count: 0,
  is_free: false,
  is_near_limit: false,
  is_at_limit: false,
  mode: "pooled",
  period_month: "",
};

const DEFAULT_HEALTH: MessagingHealth = {
  pooled_circuit_open: false,
  pooled_circuit_open_at: null,
  iugis_messaging_active: false,
  iugis_ready: false,
  iugis_readiness: {
    ready: false,
    has_token: false,
    has_channel_key: false,
    has_template_created: false,
    has_template_reminder: false,
    has_template_club_join: false,
    missing: [],
  },
};

const MessagingSettingsPage = () => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchasingSku, setPurchasingSku] = useState<string | null>(null);
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [switchToIugisModalOpen, setSwitchToIugisModalOpen] = useState(false);
  const [previewType, setPreviewType] = useState<"created" | "reminder" | "review_request">("created");
  const [preview, setPreview] = useState<MessagingOutboundPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [settings, setSettings] = useState<MessagingSettings>(DEFAULT_SETTINGS);
  const [usage, setUsage] = useState<MessagingUsage>(DEFAULT_USAGE);
  const [health, setHealth] = useState<MessagingHealth>(DEFAULT_HEALTH);
  const [packCatalog, setPackCatalog] = useState<MessagingPackCatalogItem[]>([]);
  const [initialSettings, setInitialSettings] = useState<MessagingSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    document.title = t("settings.messaging.pageTitle");
  }, [t]);

  const applyResponse = useCallback((data: MessagingSettingsData) => {
    setSettings(data.settings);
    setUsage(data.usage);
    setHealth(data.health);
    setPackCatalog(data.pack_catalog);
    setInitialSettings(data.settings);
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getMessagingSettings();
      applyResponse(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.messaging.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [applyResponse, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadPreview = useCallback(async () => {
    if (!health.iugis_messaging_active) {
      setPreview(null);
      return;
    }

    setLoadingPreview(true);

    try {
      const hoursBefore =
        previewType === "reminder"
          ? (settings.appointment_reminder_hours_before_list[0]
            ?? settings.appointment_reminder_hours_before
            ?? 24)
          : 24;
      const response = await getMessagingOutboundPreview(previewType, hoursBefore);
      setPreview(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("settings.messaging.preview.loadError"));
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [
    health.iugis_messaging_active,
    previewType,
    settings.appointment_reminder_hours_before,
    settings.appointment_reminder_hours_before_list,
    t,
  ]);

  useEffect(() => {
    if (!loading && health.iugis_messaging_active) {
      loadPreview();
    }
  }, [health.iugis_messaging_active, loadPreview, loading, previewType]);

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [initialSettings, settings]
  );

  const handleSave = async () => {
    setSaving(true);

    try {
      const response = await updateMessagingSettings(settings);
      applyResponse(response.data);
      toast.success(t("settings.messaging.toast.saveSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("settings.messaging.toast.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handlePurchasePack = async (sku: string) => {
    setPurchasingSku(sku);

    try {
      const response = await purchaseMessagingPack(sku);
      applyResponse(response.data);
      setPackModalOpen(false);
      toast.success(t("settings.messaging.packs.purchaseSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("settings.messaging.packs.purchaseError"));
    } finally {
      setPurchasingSku(null);
    }
  };

  const usageLabel = usage.is_free
    ? t("settings.messaging.usage.freeBlocked")
    : t("settings.messaging.usage.paid", {
        count: usage.sent_count,
        limit: usage.effective_limit,
      });

  const renderUsageAlert = () => {
    if (usage.is_free) {
      return (
        <Alert color="danger" className="mb-0">
          {t("settings.messaging.usage.freeBanner")}{" "}
          <Link to="/settings/subscription" className="alert-link">
            {t("settings.messaging.usage.upgradeLink")}
          </Link>
        </Alert>
      );
    }

    if (usage.is_at_limit) {
      return (
        <Alert color="danger" className="mb-0">
          {t("settings.messaging.usage.atLimit", {
            count: usage.sent_count,
            limit: usage.effective_limit,
          })}
        </Alert>
      );
    }

    if (isWhatsAppUsageNearLimit(usage)) {
      return (
        <Alert color="warning" className="mb-0">
          {t("settings.messaging.usage.nearLimit", {
            count: usage.sent_count,
            limit: usage.effective_limit,
          })}
        </Alert>
      );
    }

    if (usage.pack_bonus > 0) {
      return (
        <p className="text-muted small mb-0">
          {t("settings.messaging.usage.packBonus", { bonus: usage.pack_bonus })}{" "}
          {t("settings.messaging.usage.remaining", { count: usage.remaining_count })}
        </p>
      );
    }

    return (
      <p className="text-muted small mb-0">
        {t("settings.messaging.usage.paidHint")}{" "}
        {t("settings.messaging.usage.remaining", { count: usage.remaining_count })}
      </p>
    );
  };

  const setChannelMode = (mode: MessagingChannelMode) => {
    setSettings((current) => ({
      ...current,
      messaging_channel_mode: mode,
    }));
  };

  const requestChannelMode = (mode: MessagingChannelMode) => {
    if (
      mode === "tenant_number"
      && initialSettings.messaging_channel_mode === "pooled"
      && settings.messaging_channel_mode !== "tenant_number"
      && (usage.remaining_count > 0 || usage.pack_bonus > 0)
    ) {
      setSwitchToIugisModalOpen(true);
      return;
    }

    setChannelMode(mode);
  };

  const confirmSwitchToIugis = () => {
    setChannelMode("tenant_number");
    setSwitchToIugisModalOpen(false);
  };

  const setChannelPreference = (preference: NotificationChannelPreference) => {
    setSettings((current) => ({
      ...current,
      notification_channel_preference: preference,
    }));
  };

  const whatsAppDisabled = settings.messaging_channel_mode === "disabled";
  const isTenantNumberMode = settings.messaging_channel_mode === "tenant_number";
  const poolBlocked = isWhatsAppUsageBlocked(usage);
  const showIugisCta =
    !health.iugis_messaging_active && settings.messaging_channel_mode === "pooled";

  const iugisSelectedReminderCount = settings.appointment_reminder_hours_before_list.length;

  const toggleIugisReminderHour = (hours: number, checked: boolean) => {
    setSettings((current) => {
      const currentList = [...current.appointment_reminder_hours_before_list].sort((a, b) => a - b);

      if (checked) {
        if (currentList.includes(hours) || currentList.length >= MAX_IUGIS_REMINDER_HOURS) {
          return current;
        }

        return {
          ...current,
          appointment_reminder_hours_before_list: [...currentList, hours].sort((a, b) => a - b),
        };
      }

      const nextList = currentList.filter((value) => value !== hours);

      return {
        ...current,
        appointment_reminder_hours_before_list: nextList.length > 0 ? nextList : [24],
      };
    });
  };

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <Container fluid>
        <BreadCrumb
          title={t("settings.messaging.breadcrumb.title")}
          pageTitle={t("settings.messaging.breadcrumb.pageTitle")}
        />

        <p className="text-muted mb-4">{t("settings.messaging.pageHelper")}</p>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            <Col lg={8}>
              {health.pooled_circuit_open ? (
                <Alert color="danger" className="mb-4">
                  {t("settings.messaging.health.circuitOpen")}
                </Alert>
              ) : null}

              {showIugisCta ? (
                <Alert color="info" className="mb-4">
                  {t("settings.messaging.health.connectIugisHint")}{" "}
                  <Link to="/settings/integrations" className="alert-link">
                    {t("settings.messaging.health.connectIugisCta")}
                  </Link>
                </Alert>
              ) : null}

              {health.iugis_messaging_active && !health.iugis_ready ? (
                <Alert color="warning" className="mb-4">
                  {t("settings.messaging.health.iugisNotReady")}{" "}
                  <Link to="/settings/integrations" className="alert-link">
                    {t("settings.messaging.health.connectIugisCta")}
                  </Link>
                  {health.iugis_readiness?.missing?.length ? (
                    <span className="d-block small mt-1">
                      {t("settings.messaging.health.iugisMissing", {
                        fields: health.iugis_readiness.missing.join(", "),
                      })}
                    </span>
                  ) : null}
                </Alert>
              ) : null}

              {health.iugis_messaging_active ? (
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.messaging.templates.title")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      {t("settings.messaging.templates.hint")}
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      <Badge
                        color={
                          health.iugis_readiness?.has_template_created
                            ? "success"
                            : "secondary"
                        }
                        pill
                      >
                        {health.iugis_readiness?.has_template_created
                          ? t("settings.messaging.templates.createdOn")
                          : t("settings.messaging.templates.createdOff")}
                      </Badge>
                      <Badge
                        color={
                          health.iugis_readiness?.has_template_reminder
                            ? "success"
                            : "secondary"
                        }
                        pill
                      >
                        {health.iugis_readiness?.has_template_reminder
                          ? t("settings.messaging.templates.reminderOn")
                          : t("settings.messaging.templates.reminderOff")}
                      </Badge>
                      <Badge
                        color={
                          health.iugis_readiness?.has_template_club_join
                            ? "success"
                            : "secondary"
                        }
                        pill
                      >
                        {health.iugis_readiness?.has_template_club_join
                          ? t("settings.messaging.templates.clubJoinOn")
                          : t("settings.messaging.templates.clubJoinOff")}
                      </Badge>
                    </div>
                    {!health.iugis_readiness?.has_template_club_join ? (
                      <p className="text-muted small mb-0 mt-3">
                        {t("settings.messaging.templates.clubJoinHint")}{" "}
                        <Link to="/settings/integrations">
                          {t("settings.messaging.health.connectIugisCta")}
                        </Link>
                      </p>
                    ) : null}
                  </CardBody>
                </Card>
              ) : null}

              <Card className="mb-4">
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">{t("settings.messaging.usage.title")}</h5>
                  {usage.is_free ? (
                    <Badge color="secondary">{t("settings.messaging.usage.freeBadge")}</Badge>
                  ) : null}
                </CardHeader>
                <CardBody>
                  <p className="mb-2">{usageLabel}</p>
                  {renderUsageAlert()}
                  {!usage.is_free && settings.messaging_channel_mode === "pooled" && !isTenantNumberMode ? (
                    <div className="mt-3">
                      <Button color="soft-primary" size="sm" onClick={() => setPackModalOpen(true)}>
                        {t("settings.messaging.packs.cta")}
                      </Button>
                    </div>
                  ) : null}
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.messaging.typeA.title")}</h5>
                </CardHeader>
                <CardBody>
                  <Alert color="info" className="mb-3">
                    {t("settings.messaging.typeA.banner")}
                  </Alert>
                  <div className="form-check form-switch mb-3">
                    <Input
                      id="wa-created-enabled"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.appointment_created_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          appointment_created_notifications_enabled: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="wa-created-enabled">
                      {t("settings.messaging.typeA.toggle")}
                    </Label>
                  </div>
                  <div className="form-check form-switch">
                    <Input
                      id="wa-notify-created-default"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.notify_on_created_default}
                      disabled={!settings.appointment_created_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          notify_on_created_default: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="wa-notify-created-default">
                      {t("settings.messaging.typeA.default")}
                    </Label>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.messaging.typeB.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-4">{t("settings.messaging.typeB.help")}</p>
                  {isTenantNumberMode ? (
                    <div className="mb-3">
                      {settings.appointment_reminder_notifications_enabled ? (
                        <Badge color="primary">
                          {t("settings.messaging.typeB.activeRemindersBadge", {
                            count: iugisSelectedReminderCount,
                            max: MAX_IUGIS_REMINDER_HOURS,
                          })}
                        </Badge>
                      ) : (
                        <Badge color="secondary">
                          {t("settings.messaging.typeB.remindersDisabledBadge")}
                        </Badge>
                      )}
                    </div>
                  ) : null}
                  <div className="form-check form-switch mb-3">
                    <Input
                      id="wa-reminder-enabled"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.appointment_reminder_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          appointment_reminder_notifications_enabled: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="wa-reminder-enabled">
                      {t("settings.messaging.typeB.toggle")}
                    </Label>
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="messaging-reminder-hours">
                      {isTenantNumberMode
                        ? t("settings.messaging.typeB.hoursListLabel")
                        : t("settings.messaging.typeB.hoursLabel")}
                    </Label>
                    {isTenantNumberMode ? (
                      <>
                        <p className="text-muted small mb-2">
                          {t("settings.messaging.typeB.hoursListHelp", {
                            max: MAX_IUGIS_REMINDER_HOURS,
                          })}
                        </p>
                        <div className="d-flex flex-wrap gap-3">
                          {IUGIS_REMINDER_HOUR_OPTIONS.map((hours) => {
                            const checked = settings.appointment_reminder_hours_before_list.includes(hours);
                            const maxReached =
                              !checked
                              && settings.appointment_reminder_hours_before_list.length >= MAX_IUGIS_REMINDER_HOURS;

                            return (
                              <div className="form-check" key={hours}>
                                <Input
                                  id={`iugis-reminder-hour-${hours}`}
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checked}
                                  disabled={
                                    !settings.appointment_reminder_notifications_enabled || maxReached
                                  }
                                  onChange={(event) =>
                                    toggleIugisReminderHour(hours, event.target.checked)
                                  }
                                />
                                <Label className="form-check-label" htmlFor={`iugis-reminder-hour-${hours}`}>
                                  {t("settings.messaging.typeB.hoursOption", { count: hours })}
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <Input
                        id="messaging-reminder-hours"
                        type="select"
                        value={String(settings.appointment_reminder_hours_before)}
                        disabled={!settings.appointment_reminder_notifications_enabled}
                        onChange={(event) =>
                          setSettings((current) => ({
                            ...current,
                            appointment_reminder_hours_before: Number(event.target.value),
                          }))
                        }
                      >
                        {MESSAGING_REMINDER_HOUR_PRESETS.map((hours) => (
                          <option key={hours} value={hours}>
                            {t("settings.messaging.typeB.hoursOption", { count: hours })}
                          </option>
                        ))}
                      </Input>
                    )}
                  </div>
                  <div className="form-check form-switch">
                    <Input
                      id="wa-notify-reminder-default"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={settings.notify_reminder_default}
                      disabled={!settings.appointment_reminder_notifications_enabled}
                      onChange={(event) =>
                        setSettings((current) => ({
                          ...current,
                          notify_reminder_default: event.target.checked,
                        }))
                      }
                    />
                    <Label className="form-check-label" htmlFor="wa-notify-reminder-default">
                      {t("settings.messaging.typeB.default")}
                    </Label>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.messaging.mode.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-4">{t("settings.messaging.mode.help")}</p>

                  <div className="form-check mb-3">
                    <Input
                      id="messaging-mode-pooled"
                      type="radio"
                      name="messaging_channel_mode"
                      className="form-check-input"
                      checked={settings.messaging_channel_mode === "pooled"}
                      onChange={() => requestChannelMode("pooled")}
                    />
                    <Label className="form-check-label" htmlFor="messaging-mode-pooled">
                      {t("settings.messaging.mode.pooled")}
                    </Label>
                    {poolBlocked && settings.messaging_channel_mode === "pooled" ? (
                      <p className="text-muted small mb-0 ms-4">
                        {t("settings.messaging.mode.pooledBlockedHint")}
                      </p>
                    ) : null}
                  </div>

                  <div className="form-check mb-3">
                    <Input
                      id="messaging-mode-tenant"
                      type="radio"
                      name="messaging_channel_mode"
                      className="form-check-input"
                      checked={settings.messaging_channel_mode === "tenant_number"}
                      disabled={!health.iugis_messaging_active || !health.iugis_ready}
                      onChange={() => requestChannelMode("tenant_number")}
                    />
                    <Label
                      className={`form-check-label ${!health.iugis_messaging_active ? "text-muted" : ""}`}
                      htmlFor="messaging-mode-tenant"
                    >
                      {t("settings.messaging.mode.tenantNumber")}
                    </Label>
                    {!health.iugis_messaging_active ? (
                      <p className="text-muted small mb-0 ms-4">
                        {t("settings.messaging.mode.tenantNumberDisabledHint")}{" "}
                        <Link to="/settings/integrations" className="text-muted">
                          {t("settings.messaging.health.connectIugisCta")}
                        </Link>
                      </p>
                    ) : null}
                  </div>

                  <div className="form-check">
                    <Input
                      id="messaging-mode-disabled"
                      type="radio"
                      name="messaging_channel_mode"
                      className="form-check-input"
                      checked={settings.messaging_channel_mode === "disabled"}
                      onChange={() => setChannelMode("disabled")}
                    />
                    <Label className="form-check-label" htmlFor="messaging-mode-disabled">
                      {t("settings.messaging.mode.disabled")}
                    </Label>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.messaging.preference.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-4">{t("settings.messaging.preference.help")}</p>

                  <div className="form-check mb-3">
                    <Input
                      id="channel-pref-both"
                      type="radio"
                      name="notification_channel_preference"
                      className="form-check-input"
                      checked={settings.notification_channel_preference === "both"}
                      onChange={() => setChannelPreference("both")}
                    />
                    <Label className="form-check-label" htmlFor="channel-pref-both">
                      {t("settings.messaging.preference.both")}{" "}
                      <Badge color="success" className="ms-1">
                        {t("settings.messaging.preference.recommended")}
                      </Badge>
                    </Label>
                  </div>

                  <div className="form-check mb-3">
                    <Input
                      id="channel-pref-whatsapp"
                      type="radio"
                      name="notification_channel_preference"
                      className="form-check-input"
                      checked={settings.notification_channel_preference === "whatsapp_only"}
                      disabled={whatsAppDisabled}
                      onChange={() => setChannelPreference("whatsapp_only")}
                    />
                    <Label className="form-check-label" htmlFor="channel-pref-whatsapp">
                      {t("settings.messaging.preference.whatsappOnly")}
                    </Label>
                  </div>

                  <div className="form-check">
                    <Input
                      id="channel-pref-email"
                      type="radio"
                      name="notification_channel_preference"
                      className="form-check-input"
                      checked={settings.notification_channel_preference === "email_only"}
                      onChange={() => setChannelPreference("email_only")}
                    />
                    <Label className="form-check-label" htmlFor="channel-pref-email">
                      {t("settings.messaging.preference.emailOnly")}
                    </Label>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">{t("settings.messaging.contact.title")}</h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted small mb-3">{t("settings.messaging.contact.help")}</p>
                  <Label htmlFor="client-contact-whatsapp">{t("settings.messaging.contact.label")}</Label>
                  <Input
                    id="client-contact-whatsapp"
                    type="tel"
                    inputMode="numeric"
                    value={settings.client_contact_whatsapp ?? ""}
                    placeholder={t("settings.messaging.contact.placeholder")}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        client_contact_whatsapp:
                          event.target.value.replace(/\D/g, "") === ""
                            ? null
                            : event.target.value.replace(/\D/g, ""),
                      }))
                    }
                  />
                </CardBody>
              </Card>

              <div className="d-flex justify-content-end gap-2">
                <Button color="light" onClick={loadSettings} disabled={saving}>
                  {t("settings.messaging.actions.reload")}
                </Button>
                <Button color="primary" onClick={handleSave} disabled={!isDirty || saving}>
                  {saving ? t("settings.messaging.actions.saving") : t("settings.messaging.actions.save")}
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              {health.iugis_messaging_active ? (
                <Card className="mb-4">
                  <CardHeader className="d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">{t("settings.messaging.preview.title")}</h6>
                    <Button color="link" className="p-0" size="sm" onClick={loadPreview} disabled={loadingPreview}>
                      {loadingPreview
                        ? t("settings.messaging.preview.loading")
                        : t("settings.messaging.preview.refresh")}
                    </Button>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">{t("settings.messaging.preview.help")}</p>
                    <div className="btn-group btn-group-sm mb-3 flex-wrap" role="group">
                      <Button
                        color={previewType === "created" ? "primary" : "light"}
                        onClick={() => setPreviewType("created")}
                      >
                        {t("settings.messaging.preview.typeCreated")}
                      </Button>
                      <Button
                        color={previewType === "reminder" ? "primary" : "light"}
                        onClick={() => setPreviewType("reminder")}
                      >
                        {t("settings.messaging.preview.typeReminder")}
                      </Button>
                      <Button
                        color={previewType === "review_request" ? "primary" : "light"}
                        onClick={() => setPreviewType("review_request")}
                      >
                        {t("settings.messaging.preview.typeReviewRequest")}
                      </Button>
                    </div>
                    {loadingPreview && !preview ? (
                      <div className="text-center py-3">
                        <Spinner size="sm" color="primary" />
                      </div>
                    ) : preview ? (
                      <div className="border rounded p-3 bg-light-subtle small mb-0">
                        {preview.body}
                      </div>
                    ) : (
                      <p className="text-muted small mb-0">{t("settings.messaging.preview.empty")}</p>
                    )}
                  </CardBody>
                </Card>
              ) : null}
              <Card>
                <CardBody>
                  <Badge
                    color={
                      settings.messaging_channel_mode === "pooled" && !whatsAppDisabled
                        ? "success"
                        : "secondary"
                    }
                    className="mb-3"
                  >
                    {settings.messaging_channel_mode === "pooled" && !whatsAppDisabled
                      ? t("settings.messaging.status.poolActive")
                      : t("settings.messaging.status.poolInactive")}
                  </Badge>
                  {health.iugis_messaging_active ? (
                    <Badge color="primary" className="mb-3 ms-2">
                      {t("settings.messaging.status.iugisActive")}
                    </Badge>
                  ) : null}
                  <p className="text-muted small mb-3">{t("settings.messaging.sidebarHint")}</p>
                  <Link to="/settings/email-notifications" className="btn btn-soft-primary btn-sm me-2">
                    {t("settings.messaging.sidebarEmailLink")}
                  </Link>
                  {!health.iugis_messaging_active ? (
                    <Link to="/settings/integrations" className="btn btn-soft-secondary btn-sm">
                      {t("settings.messaging.health.connectIugisCta")}
                    </Link>
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      <Modal isOpen={packModalOpen} toggle={() => !purchasingSku && setPackModalOpen(false)} centered>
        <ModalHeader toggle={() => !purchasingSku && setPackModalOpen(false)}>
          {t("settings.messaging.packs.modalTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">{t("settings.messaging.packs.modalBody")}</p>
          <ListGroup flush>
            {packCatalog.map((pack) => (
              <ListGroupItem
                key={pack.sku}
                className="d-flex align-items-center justify-content-between px-0"
              >
                <div>
                  <p className="mb-0 fw-medium">{t(pack.label_key, { count: pack.bonus })}</p>
                  <p className="text-muted small mb-0">
                    {formatMoneyFromCents(pack.price_cents, i18n.language)}
                  </p>
                </div>
                <Button
                  color="primary"
                  size="sm"
                  disabled={purchasingSku !== null}
                  onClick={() => handlePurchasePack(pack.sku)}
                >
                  {purchasingSku === pack.sku
                    ? t("settings.messaging.packs.purchasing")
                    : t("settings.messaging.packs.buy")}
                </Button>
              </ListGroupItem>
            ))}
          </ListGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={purchasingSku !== null} onClick={() => setPackModalOpen(false)}>
            {t("settings.messaging.packs.modalClose")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={switchToIugisModalOpen}
        toggle={() => setSwitchToIugisModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setSwitchToIugisModalOpen(false)}>
          {t("settings.messaging.switchToIugis.modalTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">{t("settings.messaging.switchToIugis.modalBody")}</p>
          {usage.pack_bonus > 0 ? (
            <Alert color="warning" className="py-2 px-3 mb-2">
              {t("settings.messaging.switchToIugis.packBonusWarning", { bonus: usage.pack_bonus })}
            </Alert>
          ) : null}
          {usage.remaining_count > 0 ? (
            <p className="text-muted small mb-0">
              {t("settings.messaging.switchToIugis.remainingWarning", {
                count: usage.remaining_count,
              })}
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setSwitchToIugisModalOpen(false)}>
            {t("settings.messaging.switchToIugis.cancel")}
          </Button>
          <Button color="primary" onClick={confirmSwitchToIugis}>
            {t("settings.messaging.switchToIugis.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MessagingSettingsPage;
